import { useState, type FC } from "react"
import { Trans } from "react-i18next"
import { Navigate, useParams } from "react-router"
import styled, { css } from "styled-components"

import HostedVideoPlayer from "@forento/shared-client/components/HostedVideoPlayer"
import LexicalViewer from "@forento/shared-client/components/LexicalViewer"
import Tooltip from "@forento/shared-client/components/Tooltip"
import { LoomPlayer, VimeoPlayer, YoutubePlayer } from "@forento/shared-client/components/VideoPlayer"
import { type DetailedCoursePage } from "@forento/shared/models/course"
import { toDateTimeString, toRelativeDateString } from "@forento/shared/utilities/date"
import { parseNumber } from "@forento/shared/utilities/number"
import { exhaustiveGuard } from "@forento/shared/utilities/switch"

import { useCoursePlayer } from "~/api/course"
import { usePlatform } from "~/contexts/PlatformContext"
import CourseComments from "~/themes/school/components/CourseComments"
import FullLoadingPage from "~/themes/school/components/FullLoadingPage"
import { ArrowIcon, SkipIcon } from "~/themes/school/components/Icon"
import Layout from "~/themes/school/components/Layout"
import { PageBreadcrumb } from "~/themes/school/components/PageHeader"
import Resources from "~/themes/school/components/Resources"
import { ControlledTabs } from "~/themes/school/components/Tabs"
import { fonts } from "~/themes/school/styles"
import { useLocale, useTranslation } from "~/translations"
import routes from "~/utilities/routes"

import { Button, Buttons, PageContent } from "../shared/styles"
import Attachments from "./Attachments"
import CertificateImage from "./CertificateImage"
import ChaptersList from "./ChaptersList"
import InputAnswerType from "./answers/InputAnswerType"
import MultiAnswerType from "./answers/MultiAnswerType"
import SingleAnswerType from "./answers/SingleAnswerType"

type LoadedCoursePlayer = Extract<ReturnType<typeof useCoursePlayer>, { status: "success" }>

const CoursePlayerPage: FC = () => {
	const courseId = parseNumber(useParams().courseId!)
	const t = useTranslation()

	const player = useCoursePlayer(courseId ?? -1)

	if (player.status === "not-found") return <Navigate to={routes.course.index()} />

	return (
		<Layout>
			<PageBreadcrumb
				path={[{ title: t("course.title"), link: routes.course.index() }]}
				title={player.course?.title ?? "..."}
			/>
			{player.status === "error" ? (
				t("failedToLoad")
			) : player.status === "loading" ? (
				<FullLoadingPage />
			) : (
				<Content player={player} />
			)}
		</Layout>
	)
}

const Content: FC<{ player: LoadedCoursePlayer }> = ({ player }) => {
	const platform = usePlatform().platform
	const t = useTranslation()
	const locale = useLocale()

	const [isCertificateShown, setCertificateShown] = useState(false)

	const tabs = [
		{
			title: t("course.chapters"),
			content: (
				<ChaptersList
					courseId={player.course.id}
					currentPageId={player.current?.status === "unlocked" ? player.current.page.id : null}
					firstUncompletedPageId={player.course.firstUncompletedPage?.id ?? null}
					isFreeNavigation={player.course.isFreeNavigation}
					setPage={player.setPageById}
				/>
			),
		},
	]
	if (player.course.commentsEnabled) {
		tabs.push({
			title: t("course.comments"),
			content: (
				<CourseComments
					courseId={player.course.id}
					pageId={player.current?.status === "unlocked" ? player.current.page.id : null}
					commentsUnderReview={player.course.commentsUnderReview}
					reloadCourse={player.reloadCourse}
				/>
			),
		})
	}
	if (player.course.resources.length > 0) {
		tabs.push({ title: t("course.resources"), content: <Resources resources={player.course.resources} /> })
	}

	return (
		<>
			<PageContent $showBackground={platform.theme.id === "modern" && platform.theme.showCoursePageBackground}>
				<Spacer />
				{player.current === null ? (
					player.course.hasExam ? (
						<>
							<CourseCompletedTitle>{t("course.completedText")}</CourseCompletedTitle>
							<ExamText>{t("course.exam.readyText")}</ExamText>
							<Buttons>
								<Button variant="secondary" onClick={player.previousPage}>
									{t("course.previous")}
								</Button>
								<Button variant="primary" onClick={routes.course.exam(player.course.id)}>
									{t("course.exam.start")}
								</Button>
							</Buttons>
						</>
					) : platform.content.certificates && player.course.issueCertificates && !isCertificateShown ? (
						<>
							<CertificateImage />
							<CourseCompletedTitle>{t("certificate.received")}</CourseCompletedTitle>
							<Buttons>
								<Button variant="secondary" onClick={player.previousPage}>
									{t("course.previous")}
								</Button>
								<Button variant="secondary" onClick={routes.certificate.index()}>
									{t("certificate.goToAll")}
								</Button>
								<Button variant="primary" onClick={() => setCertificateShown(true)}>
									{t("course.continue")}
								</Button>
							</Buttons>
						</>
					) : (
						<>
							<CourseCompletedTitle>{t("course.completedText")}</CourseCompletedTitle>
							<Buttons>
								<Button variant="secondary" onClick={player.previousPage}>
									{t("course.previous")}
								</Button>
								<Button variant="primary" onClick={routes.course.index()}>
									{t("course.goToAll")}
								</Button>
							</Buttons>
						</>
					)
				) : player.current.status === "locked" ? (
					<>
						<CourseCompletedTitle>{t("course.pageLocked")}</CourseCompletedTitle>
						<Buttons>
							<Button variant="secondary" onClick={player.previousPage}>
								{t("course.previous")}
							</Button>
							<Button variant="primary" onClick={routes.course.index()}>
								{t("course.goToAll")}
							</Button>
						</Buttons>
					</>
				) : player.current.status === "not-dripped" ? (
					<>
						<PageTitle>{t("course.chapterNotDripped")}</PageTitle>
						<PageText>
							<Trans
								i18nKey="course.chapterNotDrippedText"
								values={{ relativeDate: toRelativeDateString(player.current.dripDate, locale) }}
								components={{
									tooltip: (
										<DripDateTooltip inline tooltip={toDateTimeString(player.current.dripDate)} />
									),
								}}
							/>
						</PageText>
						<Buttons>
							<Button variant="secondary" onClick={player.previousPage}>
								{t("course.previous")}
							</Button>
							<Button variant="primary" onClick={routes.course.index()}>
								{t("course.goToAll")}
							</Button>
						</Buttons>
					</>
				) : player.current.page.type === "custom" ? (
					<CustomPage
						page={player.current.page}
						isNextPageEnabled={player.isNextPageEnabled}
						onNextPage={player.nextPage}
						onPreviousPage={player.previousPage}
						onSkipChapter={player.skipChapter}
					/>
				) : player.current.page.type === "youtube" ? (
					<>
						<VideoContainer>
							<YoutubePlayer videoId={player.current.page.youTubeVideoId} />
						</VideoContainer>
						<PageTitle>{player.current.page.title}</PageTitle>
						{player.current.page.htmlContent !== null && (
							<LexicalViewer html={player.current.page.htmlContent} className="mb-6 max-w-lg" />
						)}
						<Attachments links={player.current.page.links} attachments={player.current.page.attachments} />
						<Buttons>
							<PreviousButton variant="secondary" onClick={player.previousPage}>
								{t("course.previous")}
							</PreviousButton>
							<NextButton variant="primary" onClick={player.nextPage}>
								{t("course.continue")}
							</NextButton>
							<SkipChapterButton variant="secondary" onClick={player.skipChapter}>
								{t("course.skipChapter")}
							</SkipChapterButton>
						</Buttons>
					</>
				) : player.current.page.type === "vimeo" ? (
					<>
						<VideoContainer>
							<VimeoPlayer videoId={player.current.page.vimeoVideoId} />
						</VideoContainer>
						<PageTitle>{player.current.page.title}</PageTitle>
						{player.current.page.htmlContent !== null && (
							<LexicalViewer html={player.current.page.htmlContent} className="mb-6 max-w-lg" />
						)}
						<Attachments links={player.current.page.links} attachments={player.current.page.attachments} />
						<Buttons>
							<PreviousButton variant="secondary" onClick={player.previousPage}>
								{t("course.previous")}
							</PreviousButton>
							<NextButton variant="primary" onClick={player.nextPage}>
								{t("course.continue")}
							</NextButton>
							<SkipChapterButton variant="secondary" onClick={player.skipChapter}>
								{t("course.skipChapter")}
							</SkipChapterButton>
						</Buttons>
					</>
				) : player.current.page.type === "loom" ? (
					<>
						<VideoContainer>
							<LoomPlayer videoId={player.current.page.loomVideoId} />
						</VideoContainer>
						<PageTitle>{player.current.page.title}</PageTitle>
						{player.current.page.htmlContent !== null && (
							<LexicalViewer html={player.current.page.htmlContent} className="mb-6 max-w-lg" />
						)}
						<Attachments links={player.current.page.links} attachments={player.current.page.attachments} />
						<Buttons>
							<PreviousButton variant="secondary" onClick={player.previousPage}>
								{t("course.previous")}
							</PreviousButton>
							<NextButton variant="primary" onClick={player.nextPage}>
								{t("course.continue")}
							</NextButton>
							<SkipChapterButton variant="secondary" onClick={player.skipChapter}>
								{t("course.skipChapter")}
							</SkipChapterButton>
						</Buttons>
					</>
				) : player.current.page.type === "video" ? (
					<>
						<VideoContainer>
							<HostedVideoPlayer video={player.current.page.video} />
						</VideoContainer>
						<PageTitle>{player.current.page.title}</PageTitle>
						{player.current.page.htmlContent !== null && (
							<LexicalViewer html={player.current.page.htmlContent} className="mb-6 max-w-lg" />
						)}
						<Attachments links={player.current.page.links} attachments={player.current.page.attachments} />
						<Buttons>
							<PreviousButton variant="secondary" onClick={player.previousPage}>
								{t("course.previous")}
							</PreviousButton>
							<NextButton variant="primary" onClick={player.nextPage}>
								{t("course.continue")}
							</NextButton>
							<SkipChapterButton variant="secondary" onClick={player.skipChapter}>
								{t("course.skipChapter")}
							</SkipChapterButton>
						</Buttons>
					</>
				) : player.current.page.type === "input" ? (
					<>
						{player.current.page.image !== null && (
							<PageImage
								src={player.current.page.image.filePath}
								$fit={player.current.page.image.contain ? "contain" : "cover"}
							/>
						)}
						<PageTitle>{player.current.page.title}</PageTitle>
						{player.current.page.htmlContent !== null && (
							<LexicalViewer html={player.current.page.htmlContent} className="mb-6 max-w-lg" />
						)}
						<Attachments links={player.current.page.links} attachments={player.current.page.attachments} />
						<Answers>
							<InputAnswerType
								key={player.current.page.id}
								inputType={player.current.page.inputType}
								onChange={player.setSelection}
							/>
						</Answers>
						<Buttons>
							<PreviousButton variant="secondary" onClick={player.previousPage}>
								<ButtonIcon as={ArrowIcon} direction="left" />
								{t("course.previous")}
							</PreviousButton>
							<NextButton
								variant="primary"
								onClick={player.nextPage}
								isDisabled={!player.isNextPageEnabled}
							>
								<ButtonIcon as={ArrowIcon} />
								{t("course.continue")}
							</NextButton>
							<SkipChapterButton variant="secondary" onClick={player.skipChapter}>
								<ButtonIcon as={SkipIcon} />
								{t("course.skipChapter")}
							</SkipChapterButton>
						</Buttons>
					</>
				) : player.current.page.type === "quiz" ? (
					<>
						{player.current.page.image !== null && (
							<PageImage
								src={player.current.page.image.filePath}
								$fit={player.current.page.image.contain ? "contain" : "cover"}
							/>
						)}
						<PageTitle>{player.current.page.title}</PageTitle>
						{player.current.page.htmlContent !== null && (
							<LexicalViewer html={player.current.page.htmlContent} className="mb-6 max-w-lg" />
						)}
						<Attachments links={player.current.page.links} attachments={player.current.page.attachments} />
						<Answers>
							{player.current.page.quiz.type === "single-answer" ? (
								<SingleAnswerType
									answers={player.current.page.quiz.answers}
									setSelection={player.setSelection}
								/>
							) : player.current.page.quiz.type === "multi-answer" ? (
								<MultiAnswerType
									answers={player.current.page.quiz.answers}
									setSelection={player.setSelection}
								/>
							) : (
								exhaustiveGuard(player.current.page.quiz.type)
							)}
						</Answers>
						<Buttons>
							<PreviousButton variant="secondary" onClick={player.previousPage}>
								<ButtonIcon as={ArrowIcon} direction="left" />
								{t("course.previous")}
							</PreviousButton>
							<NextButton
								variant="primary"
								onClick={player.nextPage}
								isDisabled={!player.isNextPageEnabled}
							>
								<ButtonIcon as={ArrowIcon} />
								{t("course.continue")}
							</NextButton>
							<SkipChapterButton variant="secondary" onClick={player.skipChapter}>
								<ButtonIcon as={SkipIcon} />
								{t("course.skipChapter")}
							</SkipChapterButton>
						</Buttons>
					</>
				) : player.current.page.type === "text" ? (
					<>
						{player.current.page.image !== null && (
							<PageImage
								src={player.current.page.image.filePath}
								$fit={player.current.page.image.contain ? "contain" : "cover"}
							/>
						)}
						<PageTitle>{player.current.page.title}</PageTitle>
						{player.current.page.htmlContent !== null && (
							<LexicalViewer html={player.current.page.htmlContent} className="mb-6 max-w-lg" />
						)}
						<Attachments links={player.current.page.links} attachments={player.current.page.attachments} />
						<Buttons>
							<PreviousButton variant="secondary" onClick={player.previousPage}>
								<ButtonIcon as={ArrowIcon} direction="left" />
								{t("course.previous")}
							</PreviousButton>
							<NextButton
								variant="primary"
								onClick={player.nextPage}
								isDisabled={!player.isNextPageEnabled}
							>
								<ButtonIcon as={ArrowIcon} />
								{t("course.continue")}
							</NextButton>
							<SkipChapterButton variant="secondary" onClick={player.skipChapter}>
								<ButtonIcon as={SkipIcon} />
								{t("course.skipChapter")}
							</SkipChapterButton>
						</Buttons>
					</>
				) : player.current.page.type === "audio" ? (
					<>
						{player.current.page.image !== null && (
							<PageImage
								src={player.current.page.image.filePath}
								$fit={player.current.page.image.contain ? "contain" : "cover"}
							/>
						)}
						<PageTitle>{player.current.page.title}</PageTitle>
						{player.current.page.htmlContent !== null && (
							<LexicalViewer html={player.current.page.htmlContent} className="mb-6 max-w-lg" />
						)}
						<audio controls src={player.current.page.audioUrl} className="mb-6 w-full max-w-md" />
						<Attachments links={player.current.page.links} attachments={player.current.page.attachments} />
						<Buttons>
							<PreviousButton variant="secondary" onClick={player.previousPage}>
								<ButtonIcon as={ArrowIcon} direction="left" />
								{t("course.previous")}
							</PreviousButton>
							<NextButton
								variant="primary"
								onClick={player.nextPage}
								isDisabled={!player.isNextPageEnabled}
							>
								<ButtonIcon as={ArrowIcon} />
								{t("course.continue")}
							</NextButton>
							<SkipChapterButton variant="secondary" onClick={player.skipChapter}>
								<ButtonIcon as={SkipIcon} />
								{t("course.skipChapter")}
							</SkipChapterButton>
						</Buttons>
					</>
				) : (
					exhaustiveGuard(player.current.page)
				)}
				<Spacer />
				<ScrollIndicator>
					<ScrollIndicatorIcon />
					{player.course.commentsEnabled && player.course.resources.length > 0
						? t("course.scrollDown.chaptersCommentsResources")
						: player.course.commentsEnabled
							? t("course.scrollDown.chaptersComments")
							: player.course.resources.length > 0
								? t("course.scrollDown.chaptersResources")
								: t("course.scrollDown.chapters")}
				</ScrollIndicator>
			</PageContent>
			<Details>
				<ControlledTabs tabs={tabs} />
			</Details>
		</>
	)
}

type CustomPageProps = {
	page: Extract<DetailedCoursePage, { type: "custom" }>
	isNextPageEnabled: boolean
	onPreviousPage(): void
	onNextPage(): void
	onSkipChapter(): void
}
const CustomPage: FC<CustomPageProps> = ({ page, isNextPageEnabled, onPreviousPage, onNextPage, onSkipChapter }) => (
	<>
		<PageTitle>{page.title}</PageTitle>
		<PageEditorJs className="editorjs" dangerouslySetInnerHTML={{ __html: page.htmlContent ?? "" }} />
		<Attachments links={page.links} attachments={page.attachments} />
		<Buttons>
			<PreviousButton variant="secondary" onClick={onPreviousPage}>
				Previous
			</PreviousButton>
			<NextButton variant="primary" onClick={onNextPage} isDisabled={!isNextPageEnabled}>
				Continue
			</NextButton>
			<SkipChapterButton variant="secondary" onClick={onSkipChapter}>
				Skip chapter
			</SkipChapterButton>
		</Buttons>
	</>
)

const VideoContainer = styled.div`
	width: 100%;
	max-width: 800px;
	margin-bottom: 24px;
`

const PageImage = styled.img.attrs({ alt: "Page illustration" })<{ $fit: "cover" | "contain" }>`
	width: 100%;
	max-width: 500px;
	border-radius: 8px;
	margin-bottom: 24px;

	${props =>
		props.$fit === "cover" &&
		css`
			aspect-ratio: 3;
			object-fit: cover;
		`}
`

const PageTitle = styled.h2`
	font-family: ${fonts.quicksand};
	font-size: 20px;
	font-weight: bold;
	text-align: center;
	margin-bottom: 24px;
`

const CourseCompletedTitle = styled(PageTitle)`
	margin-bottom: 24px;
`

const ExamText = styled.p`
	font-size: 14px;
	text-align: center;
	margin-bottom: 24px;
`

const PageEditorJs = styled.div`
	width: 100%;
	max-width: 650px;
	font-size: 14px;
	margin-bottom: 24px;
`

const PageText = styled.div`
	max-width: 500px;
	font-size: 14px;
	text-align: center;
	margin-bottom: 24px;
`

const DripDateTooltip = styled(Tooltip)`
	text-decoration: underline dotted;
`

const Answers = styled.div`
	width: 100%;
	max-width: 500px;
	margin-bottom: 32px;
`

const ButtonIcon = styled.div`
	width: 14px;
	height: 14px;
`

const PreviousButton = styled(Button).attrs({ variant: "secondary" })`
	grid-area: previous;
`

const NextButton = styled(Button).attrs({ variant: "primary" })`
	grid-area: next;
`

const SkipChapterButton = styled(Button).attrs({ variant: "secondary" })`
	grid-area: skipChapter;
`

const Spacer = styled.div`
	flex: 1 0 0;
`

const ScrollIndicator = styled.div`
	font-size: 18px;
	text-align: center;
	display: flex;
	align-items: center;
	gap: 8px;
	margin-top: 32px;

	@media (max-width: 999px) {
		display: none;
	}
`

const ScrollIndicatorIcon = styled(ArrowIcon).attrs({ direction: "down" })`
	width: 24px;
	height: 24px;
`

const Details = styled.div`
	background-color: white;
	box-shadow: 0px 4px 30px 0px rgba(223, 232, 255, 0.25);
	padding: 32px;

	@media (min-width: 1000px) {
		padding: 48px 86px;
	}
`

export default CoursePlayerPage
